import { ErrorBoundary } from 'react-error-boundary';
import type { ReactNode } from 'react';
import { useEffect } from 'react';

import ErrorFallback from '@/frontend/src/components/ErrorFallback';
import { AppProvider } from '@/frontend/src/contexts/appContext';
import type { EPortal } from '@/frontend/src/types/portal';
import type {
    EContext,
    EParentApp,
    ETracking,
    ICtaButtonInfo,
    IGeoProps,
} from '@/frontend/src/types/context';
import type { TLocalesTags } from '@/frontend/src/types/locale';
import type { TBreakpoints } from '@/frontend/src/hooks/useBreakpoint';
import { trackingService } from '@/frontend/src/helpers/tracking';
import useI18nContext from '@/frontend/src/hooks/useI18nContext';

import App from './App';

const AppWrapper = ({
    children,
    context,
    parentApp,
    portal,
    lang,
    ctaButtonInfo,
    geo,
    mobileBreakpoint,
    tracking,
}: {
    children?: ReactNode;
    context: EContext;
    portal: EPortal;
    lang: TLocalesTags;
    ctaButtonInfo: ICtaButtonInfo;
    geo: IGeoProps;
    mobileBreakpoint?: TBreakpoints;
    parentApp?: EParentApp;
    tracking?: ETracking;
}) => {
    const clearTracking = trackingService.initTracking();
    const { loading } = useI18nContext(lang);

    useEffect(() => {
        return () => {
            clearTracking();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return null;
    }

    return (
        <ErrorBoundary fallback={<ErrorFallback />}>
            <AppProvider
                context={context}
                parentApp={parentApp}
                portal={portal}
                geo={geo}
                ctaButtonInfo={ctaButtonInfo}
                mobileBreakpoint={mobileBreakpoint}
                tracking={tracking}
            >
                <App />
                {children}
            </AppProvider>
        </ErrorBoundary>
    );
};

export default AppWrapper;
